import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useContent } from "@/lib/content";

const LABELS = {
  originals: "Originals",
  faucet: "Faucet Collective",
  skate: "Skate",
  goods: "Goods",
};
const ALL = ["originals", "faucet", "skate", "goods"];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { t } = useContent();

  const order = t("collection_order").split(",").map((s) => s.trim()).filter((c) => ALL.includes(c));
  const fullOrder = [...order, ...ALL.filter((c) => !order.includes(c))];
  const LINKS = fullOrder.map((id) => ({ id, label: LABELS[id] }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, delay: location.pathname === "/" ? 1.6 : 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b-2 border-[#090909] transition-colors duration-300 ${
        scrolled ? "bg-[#F2EFEB]" : "bg-[#F2EFEB]/85 backdrop-blur-sm"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 h-16">
        <button
          data-testid="nav-logo"
          onClick={() => (location.pathname === "/" ? window.scrollTo({ top: 0, behavior: "smooth" }) : navigate("/"))}
          className="font-display text-xl md:text-2xl tracking-tight leading-none"
        >
          GOOD GRIEF<span className="text-[#FF2A00]">®</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="overline hover:text-[#FF2A00] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart-btn"
            onClick={() => setCartOpen(true)}
            className="overline border-2 border-[#090909] px-3 py-2 hover:bg-[#DFFF00] transition-colors relative"
          >
            Bag [{count}]
          </button>
          <button
            data-testid="nav-signup-cta"
            onClick={() => go("signup")}
            className="hidden md:block overline border-2 border-[#090909] px-4 py-2 bg-[#090909] text-[#F2EFEB] hover:bg-[#DFFF00] hover:text-[#090909] transition-colors"
          >
            {t("nav_join")}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden font-display text-lg"
          >
            {open ? "CLOSE" : "MENU"}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-[#090909] bg-[#F2EFEB]">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="block w-full text-left px-4 py-4 border-b border-[#090909] font-display text-2xl"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("signup")}
            className="block w-full text-left px-4 py-4 font-display text-2xl bg-[#DFFF00]"
          >
            JOIN THE LIST
          </button>
        </div>
      )}
    </motion.header>
  );
}
